<script setup lang="ts">
import { computed, inject, ref, watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuery } from '@tanstack/vue-query';
import { patientsApi } from '@/api/patients';
import { patientMonthlyMetricsApi } from '@/api/patient-monthly-metrics';
import { type SerializedUser } from '@/api/users';
import { usePermissions } from '@/composables/usePermissions';
import { useGroupFilterStore } from '@/stores/group-filter';
import { useServiceCategoryFilterStore } from '@/stores/service-category-filter';
import PatientSummaryCard from './patient-summary-card.vue';
import PatientTherapeuticLoadCard from './patient-therapeutic-load-card.vue';
import PatientExecutionCard from './patient-execution-card.vue';
import PatientCostStructureCard from './patient-cost-structure-card.vue';
import PatientCaregiverRanking from './patient-caregiver-ranking.vue';

const props = defineProps<{
  queryParams: string;
  selectedMonthText: string;
}>();

const currentUser = inject<Ref<SerializedUser>>('currentUser');

const { hasPermission } = usePermissions();
const showCosts = computed(() => hasPermission.value('shifts', 'read_financial_information'));

// The selects live in the dashboard "Filtros" card; the shared stores narrow the
// patient candidates here.
const { groupIds } = storeToRefs(useGroupFilterStore());
const { categoryIds, uncategorized } = storeToRefs(useServiceCategoryFilterStore());

const { data: patients, isPending: patientsPending } = useQuery({
  queryKey: computed(() => [
    'patients', 'forFilters', groupIds.value, categoryIds.value, uncategorized.value,
  ]),
  queryFn: () => patientsApi.indexForFilters(
    groupIds.value,
    { serviceCategoryIdIn: categoryIds.value, uncategorized: uncategorized.value },
  ),
});

// Allow deep-linking a patient via ?patient_id= (no client router, so read the
// query string directly).
function initialPatientId(): number | undefined {
  const requested = Number(new URLSearchParams(window.location.search).get('patient_id'));

  return Number.isInteger(requested) && requested > 0 ? requested : undefined;
}

const selectedPatientId = ref<number | undefined>(initialPatientId());

// Clear a selection that is not in the (filtered) candidate list — stale deep
// link, cross-company id, or a patient excluded by the group/category filters.
watch(patients, (loaded) => {
  if (!loaded || !selectedPatientId.value) return;
  if (!loaded.some((patient) => patient.id === selectedPatientId.value)) {
    selectedPatientId.value = undefined;
  }
});

const patientItems = computed(() =>
  (patients.value ?? []).map((patient) => ({
    value: patient.id,
    title: patient.dni ? `${patient.fullName} - ${patient.dni}` : patient.fullName,
  })));

// Match names as typed and RUTs regardless of dots/dash formatting.
function normalizeForSearch(text: string): string {
  return text.toLowerCase().replace(/[.-]/g, '');
}

function patientFilter(itemTitle: string, queryText: string): boolean {
  return normalizeForSearch(itemTitle).includes(normalizeForSearch(queryText));
}

const { data: metrics, isPending: metricsPending } = useQuery({
  queryKey: computed(() => [
    'patientMonthlyMetrics', selectedPatientId.value, props.queryParams,
  ]),
  queryFn: () => patientMonthlyMetricsApi.show(selectedPatientId.value!, props.queryParams),
  enabled: computed(() => !!selectedPatientId.value),
});

const showTherapeuticLoad = computed(() =>
  !!currentUser?.value?.companyCarePlanningEnabled && !!metrics.value?.therapeuticLoad);

const showCostStructure = computed(() => showCosts.value && !!metrics.value?.costs);

const CARDS_FULL_WIDTH = 12;

const cardMd = computed(() => {
  const visibleCards = 1 + (showTherapeuticLoad.value ? 1 : 0) + (showCostStructure.value ? 1 : 0);

  return CARDS_FULL_WIDTH / visibleCards;
});
</script>

<template>
  <div>
    <v-card class="pa-4 mb-4">
      <div class="d-flex align-center ga-2 flex-wrap">
        <v-autocomplete
          v-model="selectedPatientId"
          :items="patientItems"
          :loading="patientsPending"
          :custom-filter="patientFilter"
          label="Buscar paciente"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          class="patient-search"
        />
      </div>
    </v-card>

    <v-card
      v-if="!selectedPatientId"
      class="pa-8 text-center"
    >
      <v-icon
        icon="mdi-account-search-outline"
        size="48"
        color="grey"
        class="mb-2"
      />
      <p class="text-body-1 text-medium-emphasis mb-0">
        Selecciona un paciente para ver sus métricas del mes.
      </p>
    </v-card>
    <div
      v-else-if="metricsPending"
      class="d-flex justify-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
    <template v-else-if="metrics">
      <patient-summary-card :patient="metrics.patient" />
      <v-row>
        <v-col
          v-if="showTherapeuticLoad"
          cols="12"
          :md="cardMd"
        >
          <patient-therapeutic-load-card :rows="metrics.therapeuticLoad ?? []" />
        </v-col>
        <v-col
          cols="12"
          :md="cardMd"
        >
          <patient-execution-card :rows="metrics.execution" />
        </v-col>
        <v-col
          v-if="showCostStructure"
          cols="12"
          :md="cardMd"
        >
          <patient-cost-structure-card
            :costs="metrics.costs!"
            :selected-month-text="selectedMonthText"
          />
        </v-col>
      </v-row>
      <patient-caregiver-ranking
        :rows="metrics.caregiverRanking"
        :show-costs="showCosts"
        class="mt-4"
      />
    </template>
  </div>
</template>

<style scoped>
.patient-search {
  flex: 1 1 260px;
  min-width: 260px;
}

@media (max-width: 600px) {
  .patient-search {
    flex-basis: 100%;
    min-width: 100%;
  }
}
</style>
